import React, { useState, FC, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Slide,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  Divider,
  Paper,
} from "@mui/material";
import { ChevronRight } from "@mui/icons-material";
import { TransitionProps } from "@mui/material/transitions";
import { PatientBillInfoDto, DebitPermissionDto } from "../../../../api-client";
import { forwardRef, ReactElement, Ref } from "react";
import { observer } from "mobx-react";
import CreditPermission from "../components/CreditPermission/CreditPermission";
import RefundTransactionForm from "../components/CreditPermission/RefundTransactionForm";
import {
  useCreditPermissions,
  useDuePaymentByRxId,
} from "../../../../stores/Transactions/transaction.queries";

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: ReactElement<any, any>;
  },
  ref: Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface PayToPatientDialogProps {
  open: boolean;
  patientId: number;
  rxId: string;
  userId: string;
  locationId: string;
  handleReturnToPatient: (bill: PatientBillInfoDto) => Promise<void>; // For cash refunds
  onPaymentSuccess: () => void;
}

const PayToPatientDialog: FC<PayToPatientDialogProps> = observer(
  ({
    open,
    patientId,
    rxId,
    userId,
    locationId,
    handleReturnToPatient,
    onPaymentSuccess,
  }) => {
    const [openDialog, setOpenDialog] = useState({
      cash: false,
      einmalGutschrift: false,
      sepaSelection: false,
    });

    const [selectedCreditPermission, setSelectedCreditPermission] = useState<
      DebitPermissionDto | undefined
    >(undefined);
    const {
      data: creditPermissions,
      refetch: refetchCreditPermissions,
      isLoading: isLoadingPermissions,
    } = useCreditPermissions(patientId, true);
    const { data: duePaymentData, isLoading: isLoadingDuePayment } =
      useDuePaymentByRxId(rxId, patientId, true, true); // Enable query to get refund amount

    // Filter credit permissions - show multi-use always, single-use only if amount matches refund amount
    // This ensures single-use permissions are tied to the specific refund amount (similar to debit permissions)
    // Don't filter while due payment data is loading to avoid hiding permissions temporarily
    const validCreditPermissions =
      !isLoadingDuePayment && duePaymentData
        ? creditPermissions?.filter(
            (permission) =>
              !permission.isSingleUse ||
              (permission.amount &&
                duePaymentData?.dueAmount &&
                permission.amount.toString() ===
                  Math.abs(duePaymentData.dueAmount).toString()),
          ) || []
        : creditPermissions || []; // Show all while loading to avoid flickering

    const handleOpenDialog = (type: string) => {
      setOpenDialog({ ...openDialog, [type]: true });
    };

    const handleCloseFormDialog = (type: string) => {
      setOpenDialog({ ...openDialog, [type]: false });
      setSelectedCreditPermission(undefined);
      // Refetch credit permissions when closing sub-dialogs
      refetchCreditPermissions();
    };

    const handleSelectCreditPermission = (permission: DebitPermissionDto) => {
      setSelectedCreditPermission(permission);
      handleOpenDialog("sepaSelection");
    };

    // Refetch credit permissions when dialog opens
    useEffect(() => {
      if (open) {
        refetchCreditPermissions();
      }
    }, [open, refetchCreditPermissions]);

    return (
      <React.Fragment>
        {/* Show refund form when credit permission is selected */}
        {openDialog.sepaSelection && selectedCreditPermission ? (
          <RefundTransactionForm
            open={true}
            handleTransactionClose={() => {
              handleCloseFormDialog("sepaSelection");
              onPaymentSuccess();
            }}
            patientId={patientId}
            rxId={rxId}
            userId={userId}
            locationId={locationId}
            creditPermission={selectedCreditPermission}
            handleReturnToPatient={handleReturnToPatient}
          />
        ) : (
          <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={onPaymentSuccess}
            aria-describedby="alert-dialog-slide-description"
          >
            <DialogTitle>{"Rückerstattungsmethode auswählen"}</DialogTitle>
            <DialogContent sx={{ marginTop: 2 }}>
              <Grid
                container
                spacing={2}
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
              >
                {/* Cash Button */}
                <Grid item>
                  <Button onClick={() => handleOpenDialog("cash")}>
                    Bargeld
                  </Button>
                  {openDialog.cash && (
                    <RefundTransactionForm
                      open={openDialog.cash}
                      handleTransactionClose={() => {
                        handleCloseFormDialog("cash");
                        onPaymentSuccess();
                      }}
                      patientId={patientId}
                      rxId={rxId}
                      userId={userId}
                      locationId={locationId}
                      creditPermission={undefined}
                      handleReturnToPatient={handleReturnToPatient}
                    />
                  )}
                </Grid>

                {/* Einmal Gutschrift Button */}
                <Grid item>
                  <Button onClick={() => handleOpenDialog("einmalGutschrift")}>
                    Einmal Gutschrift
                  </Button>
                  {openDialog.einmalGutschrift && (
                    <CreditPermission
                      open={openDialog.einmalGutschrift}
                      handleTransactionClose={() => {
                        handleCloseFormDialog("einmalGutschrift");
                        onPaymentSuccess();
                      }}
                      patientData={{ id: patientId }}
                      rxId={rxId}
                      isSingleUse={true}
                      isEditing={false}
                      userId={userId}
                      locationId={locationId}
                    />
                  )}
                </Grid>
              </Grid>

              {/* Show existing SEPA credit permissions */}
              <Divider sx={{ my: 3 }} />
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Vorhandene SEPA Gutschrift-Berechtigungen:
                </Typography>
                {isLoadingPermissions ? (
                  <Typography variant="body2" color="textSecondary">
                    Lade Berechtigungen...
                  </Typography>
                ) : validCreditPermissions &&
                  validCreditPermissions.length > 0 ? (
                  <List sx={{ p: 0 }}>
                    {validCreditPermissions.map((permission) => (
                      <ListItem
                        key={permission.id}
                        disablePadding
                        sx={{ mb: 1.5 }}
                      >
                        <Paper
                          elevation={1}
                          sx={{
                            width: "100%",
                            transition: "all 0.2s ease-in-out",
                            cursor: "pointer",
                            "&:hover": {
                              elevation: 3,
                              transform: "translateY(-2px)",
                              backgroundColor: "action.hover",
                              boxShadow: 3,
                            },
                          }}
                        >
                          <ListItemButton
                            onClick={() =>
                              handleSelectCreditPermission(permission)
                            }
                            sx={{
                              py: 2,
                              px: 2,
                              borderRadius: 1,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <ListItemText
                              primary={
                                <Typography
                                  variant="subtitle1"
                                  fontWeight={600}
                                >
                                  {`${permission.firstName} ${permission.lastName}`}
                                </Typography>
                              }
                              secondary={
                                <Box sx={{ mt: 0.5 }}>
                                  <Typography
                                    component="div"
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    IBAN: {permission.iban}
                                  </Typography>
                                  <Typography
                                    component="div"
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mt: 0.5 }}
                                  >
                                    Mandatsreferenz: {permission.id}
                                    {permission.isSingleUse && (
                                      <Typography
                                        component="span"
                                        variant="caption"
                                        sx={{
                                          ml: 1,
                                          px: 1,
                                          py: 0.25,
                                          borderRadius: 1,
                                          backgroundColor: "info.light",
                                          color: "info.contrastText",
                                        }}
                                      >
                                        Einmalig
                                      </Typography>
                                    )}
                                    {!permission.isSingleUse && (
                                      <Typography
                                        component="span"
                                        variant="caption"
                                        sx={{
                                          ml: 1,
                                          px: 1,
                                          py: 0.25,
                                          borderRadius: 1,
                                          backgroundColor: "success.light",
                                          color: "success.contrastText",
                                        }}
                                      >
                                        Mehrfach
                                      </Typography>
                                    )}
                                  </Typography>
                                </Box>
                              }
                            />
                            <ChevronRight
                              sx={{ color: "action.active", ml: 1 }}
                            />
                          </ListItemButton>
                        </Paper>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Keine Gutschrift-Berechtigungen vorhanden. Erstellen Sie
                    eine neue mit "Dauergutschrift" im Hauptbildschirm.
                  </Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={onPaymentSuccess}>Abbrechen</Button>
            </DialogActions>
          </Dialog>
        )}
      </React.Fragment>
    );
  },
);

export default PayToPatientDialog;
